/**
 * Programmati — lista task pianificati dell'agente.
 *
 * iter v28 r387 — Fase B (User M1296+M1297). 3 tipi:
 *  - **auguri**: festività di gruppo per tutti i clienti
 *  - **match**: annuncio singolo verso lead filtrati
 *  - **promemoria**: messaggio singolo a un contatto in futuro
 *
 * UI flow:
 *  - Big input "Cosa vuoi pianificare?" (testo + voce) in cima → POST /from-voice
 *  - Lista task pending/ready in card pulite (data, tipo, dettagli)
 *  - Cancel = soft-delete (status=cancelled)
 *
 * I task READY (= già scattati ma non ancora eseguiti) hanno una badge
 * giallo + CTA "Apri ora →" che porta al flow di esecuzione.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  CalendarBlank, Sparkle, House, ChatCircleDots,
  ArrowRight, Spinner, X, Clock,
} from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import AppShell from "@/components/AppShell";
import DismissibleIntro from "@/components/DismissibleIntro";
import VoiceSchedulerInput from "@/components/VoiceSchedulerInput";

const KIND_META = {
  auguri:     { icon: Sparkle,         label: "Auguri pianificati",       color: "#7C8B6E", desc: "Festività di gruppo" },
  match:      { icon: House,           label: "Match annuncio",           color: "#3B7BBF", desc: "Annuncio ai lead interessati" },
  promemoria: { icon: ChatCircleDots,  label: "Promemoria",               color: "#C97A4A", desc: "Messaggio singolo programmato" },
};

function formatTriggerAt(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const now = new Date();
  const diffMs = d.getTime() - now.getTime();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
  const dateLabel = d.toLocaleDateString("it-IT", { day: "numeric", month: "long", year: d.getFullYear() === now.getFullYear() ? undefined : "numeric" });
  const timeLabel = d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
  if (diffDays === 0) return `Oggi · ${timeLabel}`;
  if (diffDays === 1) return `Domani · ${timeLabel}`;
  if (diffDays > 1 && diffDays <= 7) return `Tra ${diffDays} giorni · ${timeLabel}`;
  if (diffDays < 0) return `Scattato ${Math.abs(diffDays)} ${Math.abs(diffDays) === 1 ? "giorno" : "giorni"} fa · ${timeLabel}`;
  return `${dateLabel} · ${timeLabel}`;
}

export default function Programmati() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/scheduled-tasks");
      setTasks(data?.tasks || []);
    } catch (e) {
      toast.error(apiError(e, "Impossibile caricare i task"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchTasks(); }, []);

  const cancelTask = async (id) => {
    try {
      await api.delete(`/scheduled-tasks/${id}`);
      toast.success("Annullato");
      await fetchTasks();
    } catch (e) {
      toast.error(apiError(e, "Annullamento fallito"));
    }
  };

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto pb-20">
        <DismissibleIntro storageKey="programmati:intro" className="mb-6">
          <span className="overline">Programmati</span>
          <h3 className="font-editorial text-2xl sm:text-3xl mt-2">Pianifica una volta, Pronto ti avvisa al momento giusto</h3>
          <p className="text-sm text-[#6B6A65] mt-2 max-w-md">
            Auguri di gruppo, match annuncio coi lead interessati, promemoria personali.
            Dimmi cosa fare e quando — al momento giusto compare un banner sulla home.
          </p>
        </DismissibleIntro>

        {/* ═════ Input vocale ═════ */}
        <div className="mb-6">
          <VoiceSchedulerInput
            onSuccess={() => fetchTasks()}
            testIdPrefix="programmati-voice"
          />
        </div>

        {/* ═════ Lista task ═════ */}
        <div className="space-y-2.5">
          <div className="flex items-center justify-between mb-1 px-1">
            <h4 className="font-editorial text-[18px] text-[#1C1C1A]">
              {tasks.length === 0 ? "Nessun task in coda" : `${tasks.length} ${tasks.length === 1 ? "task" : "task"} pianificat${tasks.length === 1 ? "o" : "i"}`}
            </h4>
            {loading && <Spinner size={14} className="animate-spin text-[#9A968B]" />}
          </div>

          {!loading && tasks.length === 0 && (
            <div className="bg-[#F8F6F1] border border-dashed border-[#E5E7E0] rounded-xl px-5 py-8 text-center" data-testid="programmati-empty">
              <CalendarBlank size={32} weight="thin" className="mx-auto text-[#9A968B] mb-2.5" />
              <p className="text-[13px] text-[#6B6A65] max-w-md mx-auto leading-snug">
                Quando pianifichi qualcosa con la voce, lo trovi qui. Pronto ti avviserà con un banner sulla home il giorno X.
              </p>
            </div>
          )}

          {tasks.map((task) => {
            const meta = KIND_META[task.kind] || KIND_META.promemoria;
            const Icon = meta.icon;
            const isReady = task.status === "ready";
            return (
              <div
                key={task.id}
                className={`bg-white border rounded-xl p-4 flex items-start gap-3 ${
                  isReady ? "border-amber-400 bg-amber-50/40" : "border-[#E5E7E0]"
                }`}
                data-testid={`task-${task.id}`}
              >
                <div
                  className="shrink-0 w-9 h-9 rounded-full inline-flex items-center justify-center text-white"
                  style={{ background: meta.color }}
                >
                  <Icon size={16} weight="fill" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[12.5px] font-semibold text-[#1C1C1A]">{meta.label}</span>
                    {isReady && (
                      <span className="text-[9.5px] uppercase tracking-[0.14em] font-bold bg-amber-500 text-white px-1.5 py-0.5 rounded-full">
                        Pronto ora
                      </span>
                    )}
                  </div>
                  <div className="text-[11.5px] text-[#6B6A65] mt-0.5 inline-flex items-center gap-1">
                    <Clock size={10} weight="bold" /> {formatTriggerAt(task.trigger_at)}
                  </div>
                  {task.notes && (
                    <div className="text-[11px] text-[#9A968B] mt-1.5 italic line-clamp-2 leading-snug">
                      &laquo;{task.notes}&raquo;
                    </div>
                  )}
                </div>
                <div className="shrink-0 flex items-center gap-1">
                  {isReady && (
                    <button
                      type="button"
                      onClick={() => navigate("/dashboard")}
                      className="px-2.5 py-1.5 rounded-full bg-[#1C1C1A] text-white text-[10.5px] font-semibold inline-flex items-center gap-1"
                      data-testid={`task-execute-${task.id}`}
                    >
                      Apri <ArrowRight size={10} weight="bold" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => cancelTask(task.id)}
                    className="w-7 h-7 rounded-full hover:bg-[#1C1C1A]/5 inline-flex items-center justify-center text-[#9A968B] hover:text-red-600"
                    aria-label="Annulla"
                    data-testid={`task-cancel-${task.id}`}
                  >
                    <X size={12} weight="bold" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </AppShell>
  );
}
